import React, { useMemo } from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';
import { withTranslatedAssets } from '../WithTranslatedAssets/WithTranslatedAssets';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

export const CMSPageComponent = props => {
  const { params, pageAssetsData, inProgress, error, translationMeta } = props;
  const pageId = params.pageId || props.pageId;

  // Log translation info for debugging (remove in production)
  if (process.env.NODE_ENV === 'development' && translationMeta?.isTranslated) {
    console.log(`ShareGardens CMSPage (${pageId}): Using translated content`, {
      locale: translationMeta.currentLocale,
      assetsTranslated: translationMeta.assetsTranslated,
      cacheStats: translationMeta.cacheStats
    });
  }

  if (!inProgress && error?.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  return (
    <PageBuilder
      pageAssetsData={pageAssetsData?.[pageId]?.data}
      inProgress={inProgress}
      schemaType="Article"
    />
  );
};

CMSPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  translationMeta: object,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  return { pageAssetsData, inProgress, error };
};

// Create a wrapper component that dynamically determines which assets to translate
// based on the current pageId from route params
const CMSPageWithDynamicTranslation = props => {
  const { params } = props;
  const pageId = params?.pageId || props.pageId;
  
  // Dynamically create asset names array based on current page
  const assetNames = useMemo(() => {
    return pageId ? [pageId] : [];
  }, [pageId]);

  // Create the enhanced component with translation support
  const EnhancedCMSPage = useMemo(() => {
    return withTranslatedAssets({
      enableTranslation: true,
      assetNames: assetNames,
      cacheTimeoutMs: 30 * 60 * 1000 // 30 minutes cache
    })(CMSPageComponent);
  }, [assetNames]);

  return <EnhancedCMSPage {...props} />;
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const CMSPage = compose(
  withRouter,
  connect(mapStateToProps)
)(CMSPageWithDynamicTranslation);

export default CMSPage;
